// @flow
import { Box } from 'grommet';
import * as React from 'react';
import { defineMessages, IntlShape, injectIntl } from 'react-intl';
import styled from 'styled-components';
import RedButton from 'esm-web-pro/components/layout/RedButton';
import GreenButton from 'esm-web-pro/components/layout/GreenButton';
import ColorableButton from 'esm-web-pro/components/layout/ColorableButton';

type Props = {
    onClose?: Function,
    onClear?: Function,
    onDefault?: Function,
    onSave?: Function,
    onSaveAndContinue?: Function,
    intl: IntlShape,
    disabled: boolean,
    disabledReset: boolean,
    disabledDefault: boolean,
    disabledSubmit: boolean,
    saveButtonLabel?: React.Node,
    filterButtonLabel?: React.Node,
    defaultButtonLabel?: React.Node,
    clearButtonLabel?: React.Node,
    cancelButtonLabel?: React.Node,
    cancelButtonProps?: React.ElementConfig<typeof RedButton>,
    clearButtonProps?: React.ElementConfig<typeof RedButton>,
    defaultButtonProps?: React.ElementConfig<typeof ColorableButton>,
    saveButtonProps?: React.ElementConfig<typeof GreenButton>,
    background?: boolean,
}

const StyledActionBox = styled(Box)`
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 10;
`;

const messages = defineMessages({
    cancelBtn: {
        id: 'cancel-btn',
        defaultMessage: 'Cancel',
    },
    saveBtn: {
        id: 'save-btn',
        defaultMessage: 'Save',
    },
    saveAndContinueBtn: {
        id: 'save-and-continue-btn',
        defaultMessage: 'Save and add new',
    },
    clearBtn: {
        id: 'filters-btn-reset',
        defaultMessage: 'Reset',
    },
    defaultBtn: {
        id: 'filters-btn-default',
        defaultMessage: 'Default',
    },

});

const ActionButtons = (props: Props) => {
    const {
        onClose, onDefault, onSave, onSaveAndContinue, intl: { formatMessage }, disabled, saveButtonLabel,
        filterButtonLabel, cancelButtonLabel, clearButtonLabel, defaultButtonLabel, cancelButtonProps,
        defaultButtonProps, saveButtonProps, background, disabledReset, disabledDefault, disabledSubmit,
    } = props;

    return (
        <StyledActionBox
            direction="row"
            pad="small"
            style={{
                zIndex: 1,
                width: '100%',
                minWidth: '340px',
                background: 'white',
            }}

        >
            {(
                <Box
                    flex
                    height="32px"
                    pad={onSaveAndContinue ? { horizontal: 'xsmall' } : { horizontal: 'small' }}
                    width="100%"
                    style={onSaveAndContinue ? { paddingLeft: '5px', paddingRight: '5px' } : undefined}
                >
                    <RedButton
                        fill
                        label={cancelButtonLabel || clearButtonLabel || formatMessage(messages.cancelBtn)}
                        onClick={onClose}
                        color="status-error"
                        disabled={disabledReset}
                        {...cancelButtonProps}
                        style={onSaveAndContinue ? { paddingLeft: '5px', paddingRight: '5px' } : undefined}
                    />
                </Box>
            )}
            {onDefault && (
                <Box
                    flex
                    height="32px"
                    pad={onSaveAndContinue ? { horizontal: 'xsmall' } : { horizontal: 'small' }}
                    width="100%"
                >
                    <ColorableButton
                        label={defaultButtonLabel}
                        hoverIndicator={{ color: '#FDD089' }}
                        backgroundColor="#FEE7C4"
                        textColor="#966626"
                        type="button"
                        onClick={onDefault}
                        disabled={disabled || disabledDefault}
                        fill
                        {...defaultButtonProps}
                    />
                </Box>
            )}
            {onSave && (
                <Box
                    flex
                    height="32px"
                    pad={{ horizontal: 'small' }}
                    style={onSaveAndContinue ? { paddingLeft: '5px', paddingRight: '5px' } : undefined}
                >
                    <GreenButton
                        type="submit"
                        fill
                        label={saveButtonLabel || filterButtonLabel || formatMessage(messages.saveBtn)}
                        onClick={onSave}
                        disabled={disabled || disabledSubmit}
                        style={onSaveAndContinue ? { paddingLeft: '5px', paddingRight: '5px' } : undefined}
                        {...saveButtonProps}
                    />
                </Box>
            )}
            {onSaveAndContinue && (
                <Box
                    direction="row"
                    pad={{ horizontal: 'xsmall' }}
                    height="32px"
                    flex={{ shrink: 0 }}
                    style={{ paddingLeft: '5px', paddingRight: '5px' }}
                >
                    <GreenButton
                        type="submit"
                        fill
                        label={saveButtonLabel || formatMessage(messages.saveAndContinueBtn)}
                        alignSelf="end"
                        onClick={onSaveAndContinue}
                        disabled={disabled}
                        style={{ padding: '4px 8px' }}
                    />
                </Box>
            )}
        </StyledActionBox>
    );
};

ActionButtons.defaultProps = {
    disabled: false,
    cancelButtonLabel: '',
    clearButtonLabel: '',
    defaultButtonLabel: '',
    filterButtonLabel: '',
    saveButtonLabel: '',
    onSave() {},
    onClose() {},
    onClear() {},
    onDefault: undefined,
    onSaveAndContinue: undefined,
    cancelButtonProps: {},
    clearButtonProps: {},
    defaultButtonProps: {},
    saveButtonProps: {},
    background: 'white',
};

export default injectIntl(ActionButtons);
